import { captureHandledException } from '@/lib/sentry';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'wouter';
import { Search, FileText, Edit, Eye, Trash2, Plus } from 'lucide-react';
import { VideoData, CategoryData, getUserArticles, getUserCategories, updateVideo } from '@/lib/firebase';
import ArticleViewModal from '@/components/article-view-modal';
import ArticleStatsOverview from '@/components/article-stats-overview';
import { useAuth } from '@/contexts/use-auth';
import { logger } from '@/lib/logger';

interface ArticleListProps {
  articles: VideoData[];
  categories: CategoryData[];
  onEditArticle: (article: VideoData) => void;
  onViewArticle: (article: VideoData) => void;
  onDeleteArticle: (id: string) => void;
}

function ArticleList({ articles, categories, onEditArticle, onViewArticle, onDeleteArticle }: ArticleListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getEffectiveStatus = (article: VideoData) => {
    if (article.status === 'Published' && article.publishDate) {
      const publishDate = new Date(article.publishDate.toString());
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      publishDate.setHours(0, 0, 0, 0);
      
      if (publishDate > today) {
        return 'Scheduled';
      }
    }
    return article.status;
  };

  const filteredArticles = articles.filter(article => {
    const articleAuthorName = (article.contributors && article.contributors[0]?.name) || article.authorName || article.author || '';
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         articleAuthorName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesModule = !moduleFilter || (article.modules && article.modules.includes(moduleFilter));
    const effectiveStatus = getEffectiveStatus(article);
    const matchesStatus = !statusFilter || effectiveStatus === statusFilter;
    
    return matchesSearch && matchesModule && matchesStatus;
  });

  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedArticles = filteredArticles.slice(startIndex, startIndex + itemsPerPage);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Published':
        return 'default';
      case 'Scheduled':
        return 'secondary';
      case 'Draft':
        return 'outline';
      case 'Blocked':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getModuleDisplay = (modules: string[] = []) => {
    if (!modules.length) return 'No Module';
    // Capitalize first letter of module
    return modules[0].charAt(0).toUpperCase() + modules[0].slice(1);
  };

  const getModuleColors = (module: string) => {
    switch (module?.toLowerCase()) {
      case 'body':
        return {
          bg: 'bg-[#d059c4]/10',
          border: 'border-[#d059c4]/30',
          text: 'text-[#d059c4]'
        };
      case 'mind':
        return {
          bg: 'bg-[#66cee7]/10',
          border: 'border-[#66cee7]/30',
          text: 'text-[#66cee7]'
        };
      case 'general':
        return {
          bg: 'bg-[#595ebe]/10',
          border: 'border-[#595ebe]/30',
          text: 'text-[#595ebe]'
        };
      default:
        return {
          bg: 'bg-slate-100',
          border: 'border-slate-200',
          text: 'text-slate-600'
        };
    }
  };

  const formatDate = (timestamp?: any) => {
    if (!timestamp || !timestamp.seconds) return '--';
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  };

  return (
    <div className="space-y-8">
      {/* Filters and Search */}
      <Card className="border border-slate-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Select value={moduleFilter || "all"} onValueChange={(value) => setModuleFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="mind">Mind</SelectItem>
                <SelectItem value="body">Body</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Scheduled">Scheduled</SelectItem>
                <SelectItem value="Published">Published</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Article Library */}
      <Card className="border border-slate-200">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Article Library</h2>
          <Link href="/articles/create">
            <Button className="flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Add Article</span>
            </Button>
          </Link>
        </div>
        
        {paginatedArticles.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No articles found</h3>
            <p className="text-slate-600">
              {searchQuery || moduleFilter || statusFilter
                ? 'Try adjusting your filters or search terms.'
                : 'Create your first article to get started.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Article</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Module</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Modified</th>
                    <th className="text-right py-3 px-6 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {paginatedArticles.map((article) => (
                    <tr key={article.id} className="hover:bg-slate-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-16 h-10 rounded flex items-center justify-center text-white text-xs font-bold bg-green-600">
                            ARTICLE
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{article.title}</p>
                            <div className="flex items-center space-x-2 text-sm text-slate-500">
                              <span>{article.channel}</span>
                              <span>•</span>
                              <div className="flex items-center space-x-1">
                                {(() => {
                                  const contribName = (article.contributors && article.contributors[0]?.name) || article.authorName || article.author || '';
                                  const contribImg = (article.contributors && article.contributors[0]?.profilePicUrl) || article.authorImageUrl || '';
                                  return (
                                    <>
                                      {contribImg ? (
                                        <img
                                          src={contribImg}
                                          alt={contribName}
                                          className="w-4 h-4 rounded-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-4 h-4 bg-slate-300 rounded-full flex items-center justify-center">
                                          <span className="text-xs text-slate-600">
                                            {contribName.charAt(0).toUpperCase()}
                                          </span>
                                        </div>
                                      )}
                                      <span>{contribName}</span>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {article.modules && article.modules.length > 0 ? (
                          <div className={`inline-flex px-3 py-1.5 rounded-full text-xs font-medium border ${getModuleColors(article.modules[0]).bg} ${getModuleColors(article.modules[0]).border} ${getModuleColors(article.modules[0]).text}`}>
                            {getModuleDisplay(article.modules)}
                          </div>
                        ) : (
                          <div className="inline-flex px-3 py-1.5 rounded-full text-xs font-medium border bg-slate-50 border-slate-200 text-slate-600">
                            No Module
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <Badge variant={getStatusBadgeVariant(getEffectiveStatus(article))}>
                            {getEffectiveStatus(article)}
                          </Badge>
                          {(getEffectiveStatus(article) === 'Published' || getEffectiveStatus(article) === 'Scheduled') && article.publishDate && (
                            <div className="text-xs text-slate-500 mt-1">
                              {new Date(article.publishDate.toString()).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-slate-600">
                        {formatDate(article.modified)}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditArticle(article)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewArticle(article)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteArticle(article.id!)}
                            className="text-red-600 hover:text-red-700"
                            title="Disable article (soft delete)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-6 border-t border-slate-200 flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredArticles.length)} of {filteredArticles.length} articles
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default function Articles() {
  const [articles, setArticles] = useState<VideoData[]>([]);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewArticle, setViewArticle] = useState<VideoData | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  // Navigation handled by Link component

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [articlesData, categoriesData] = await Promise.all([
        getUserArticles(user!.uid),
        getUserCategories()
      ]);
      
      setArticles(articlesData);
      setCategories(categoriesData);
    } catch (error) {
      logger.error('Error loading data:', error);
      toast({
        title: "Error loading articles",
        description: "Failed to load articles and categories.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditArticle = (article: VideoData) => {
    // Navigate to edit article page with article ID
    window.location.href = `/articles/create?id=${article.id}`;
  };

  const handleViewArticle = (article: VideoData) => {
    setViewArticle(article);
  };

  const handleDeleteArticle = async (id: string) => {
    if (window.confirm('Are you sure you want to disable this article? It will be removed from view but not permanently deleted.')) {
      try {
        await updateVideo(id, { enabled: false });
        await loadData();
        toast({
          title: "Article disabled",
          description: "The article has been disabled and will no longer appear in the library.",
        });
      } catch (error) {
        captureHandledException(error, "articles.disable");
        toast({
          title: "Error disabling article",
          description: "Failed to disable the article. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">Loading articles...</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate stats for articles
  const getEffectiveStatus = (article: VideoData) => {
    if (article.status === 'Published' && article.publishDate) {
      const publishDate = new Date(article.publishDate.toString());
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      publishDate.setHours(0, 0, 0, 0);
      
      if (publishDate > today) {
        return 'Scheduled';
      }
    }
    return article.status;
  };

  const totalArticles = articles.length;
  const draft = articles.filter(a => getEffectiveStatus(a) === 'Draft').length;
  const scheduled = articles.filter(a => getEffectiveStatus(a) === 'Scheduled').length;
  const published = articles.filter(a => getEffectiveStatus(a) === 'Published').length;
  const blocked = articles.filter(a => getEffectiveStatus(a) === 'Blocked').length;

  return (
    <div className="flex-1 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Articles</h1>
          <p className="text-slate-600">Manage your article content library</p>
        </div>

        <ArticleStatsOverview
          totalArticles={totalArticles}
          draft={draft}
          scheduled={scheduled}
          published={published}
          blocked={blocked}
        />

        <ArticleList
          articles={articles}
          categories={categories}
          onEditArticle={handleEditArticle}
          onViewArticle={handleViewArticle}
          onDeleteArticle={handleDeleteArticle}
        />
        
        <ArticleViewModal
          article={viewArticle}
          isOpen={!!viewArticle}
          onClose={() => setViewArticle(null)}
        />
      </div>
    </div>
  );
}